import {
  ColorObject,
  createTheme,
  decomposeColor,
  filledInputClasses,
  iconButtonClasses,
  recomposeColor,
} from '@mui/material'
import { colors } from '@portal/pkg-ui'
import React from 'react'

declare module '@mui/material/styles' {
  interface Theme {
    buttons: {
      primaryAlt: {
        color: React.CSSProperties['color']
        background: React.CSSProperties['background']
        opacity: React.CSSProperties['opacity']
      }
    }
  }
  interface ThemeOptions {
    buttons: {
      primaryAlt?: {
        color?: React.CSSProperties['color']
        background?: React.CSSProperties['background']
        opacity?: React.CSSProperties['opacity']
      }
    }
  }
}

declare module '@mui/material/TextField' {
  interface TextFieldPropsSizeOverrides {
    extrasmall: true
  }
}

export const createMercatusTheme = () => {
  const baseColor = colors.defaultBaseColor
  const text: ColorObject = decomposeColor(baseColor)
  text.values.push(0.75)
  const background: ColorObject = decomposeColor(baseColor)
  background.values.push(0.1)

  return createTheme({
    buttons: {
      primaryAlt: {
        color: recomposeColor(text),
        background: recomposeColor(background),
      },
    },
    components: {
      // MuiInputBase: {
      //   styleOverrides: {
      //     root: {
      //       marginBottom: '8px',
      //     },
      //   },
      // },
      MuiFormHelperText: {
        styleOverrides: {
          contained: {
            marginLeft: 0,
          },
        },
      },
      // FIXME: Need to evaluate these changes, they seem specific to certain instances and should not be global.
      // Currently this is breaking seemingly every input field in the application.
      // MuiFormLabel: {
      //   defaultProps: {},
      //   styleOverrides: {
      //     root: {
      //       fontSize: '1.2rem',
      //       '&.MuiInputLabel-outlined': {
      //         left: '-12px',
      //       },
      //     },
      //   },
      // },
      // MuiOutlinedInput: {
      //   defaultProps: {
      //     size: 'small',
      //     notched: false,
      //   },
      //   styleOverrides: {
      //     root: {
      //       minHeight: 40,
      //       marginTop: '20px',
      //       marginBottom: 0,
      //       '& .MuiFormHelperText-root': {
      //         left: '-12px',
      //       },
      //       '& .MuiOutlinedInput-input': {
      //         paddingTop: 0,
      //         paddingBottom: 0,
      //       },
      //     },
      //     notchedOutline: {
      //       '& legend': {
      //         display: 'none',
      //       },
      //     },
      //   },
      // },
      MuiTextField: {
        defaultProps: {
          variant: 'standard',
          size: 'small',
        },
        variants: [
          {
            props: { size: 'extrasmall' },
            style: {
              '& .MuiInputBase-input': {
                padding: '2px',
                fontSize: '0.875rem',
              },
              '& .MuiInputBase-root': {
                height: 33,
              },
            },
          },
        ],
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontSize: '0.8rem',
          },
          body: {
            fontSize: '0.9rem',
          },
        },
      },
      MuiInput: {
        styleOverrides: {
          underline: {
            '&:before': {
              borderBottom: '1px solid #ddd',
            },
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            marginTop: 3,
            marginBottom: 3,
            backgroundColor: 'rgba(248, 248, 248, 0.75)',
            [`&.${filledInputClasses.disabled}`]: {
              backgroundColor: 'rgba(248, 248, 248, 0.5)',
            },
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            [`& .${iconButtonClasses.root}`]: {
              position: 'absolute',
              right: 15,
              top: 4,
              padding: 10,
              marginRight: -6,
            },
          },
        },
      },
      /* Don't capitalize all the button text */
      MuiButton: {
        defaultProps: {
          size: 'small',
        },
        styleOverrides: {
          root: {
            textTransform: 'none',
          },
          contained: {
            boxShadow: 'none',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransformation: 'none',
            textTransform: 'none',
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            ...(!ownerState.disableGutters && {
              padding: theme.spacing(1),
              [theme.breakpoints.up('sm')]: {
                padding: theme.spacing(1),
              },
            }),
            ...(ownerState.variant === 'dense' && {
              minHeight: 32,
            }),
          }),
        },
      },
      /* ensure the max length of a label doesn't overflow and does ellipsis */
      MuiInputLabel: {
        styleOverrides: {
          filled: {
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: '95%',
            overflow: 'hidden',
          },
        },
      },
      /* mobile dialogs should have no margin and be fullscreen */
      MuiDialog: {
        styleOverrides: {
          paper: ({ theme }) => ({
            [theme.breakpoints.down('lg')]: {
              margin: 0,
              maxHeight: 'none !important',
              height: '100%',
              width: '100%',
              flex: 1,
            },
          }),
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderLeft: '2px solid #ffffff',
            '&:hover': {
              borderLeft: '2px solid #0078c1',
              borderColor: baseColor,
            },
            '& a': {
              textDecoration: 'none !important',
            },
          },
        },
      },
    },
    palette: {
      primary: {
        main: baseColor,
      },
      secondary: {
        main: colors.typographyB,
      },
    },
  })
}

export const createDefaultMercatusTheme = () => createMercatusTheme()
